import { composeClassNames } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import SectionHeading from "src/components/SectionHeading";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import { mapContentToVideoCards } from "src/functions/mapContentToVideoCards";
import useLoggedInUser from "src/functions/useLoggedInUser";
import validateAuthToken from "src/functions/validateAuthToken";
import { sButton } from "src/styles/sButton.module.scss";
import { sButtons } from "src/styles/sButtons.module.scss";
import { sContentOverview } from "src/styles/sContentOverview.module.scss";
import dynamic from "next/dynamic";

const VideoListSlider = dynamic(() => import("src/components/VideoListSlider"));

type Props = {
  classes: any;
  className?: (string | string) | null | undefined;
  primaryButton?: { title: string; onClick: any };
  series: any;
  style?: CSSProperties | null | undefined;
};

function ContentOverview({
  className,
  classes,
  primaryButton,
  series,
  style,
}: Props) {
  const { locale: lang, push } = useRouter();
  const [activeContent, setActiveContent] = useState<
    "serie" | "class" | "festival" | "stream" | "audio"
  >("serie");

  const [numberOfLectures, setNumberOfLectures] = useState(0);
  const [numberOfPrograms, setNumberOfPrograms] = useState(0);

  useEffect(() => {
    fetch(`${constants.Paths.apiUrl}/lectures/count`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        setNumberOfLectures(await res.json());
      }
    });
  }, []);

  useEffect(() => {
    fetch(`${constants.Paths.apiUrl}/programs/count`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        setNumberOfPrograms(await res.json());
      }
    });
  }, []);

  var userResult = useLoggedInUser();
  const [activeSubscription, setActiveSubscription] = useState(null);

  useEffect(() => {
    userResult.result &&
      fetch(`${constants.Paths.apiUrl}/clients/me`, {
        method: "GET",
        headers: validateAuthToken(getCookieByName(`auth-token`)),
      }).then(async (res) => {
        if (res.ok) {
          const result = await res.json();
          const subscriptionTill = result.subscription_till;
          const today = new Date();

          if (new Date(subscriptionTill) >= today) {
            setActiveSubscription(result);
          }
        }
      });
  }, [userResult.result]);

  return (
    <div
      style={style}
      className={composeClassNames([
        sContentOverview,
        className,
        "homepage-content-wrapper",
      ])}
    >
      <SectionHeading
        align="center"
        subtitle="Try other community members' favorite lessons."
        style={{ marginBottom: constants.Size.xl }}
        title="Explore the lectures"
      />
      <div className="homepage-content-switch-wrapper">
        <div className="homepage-content-switch">
          <Button
            variant={`outline-dark`}
            size="sm"
            className={composeClassNames([
              sButton,
              `switch-button`,
              activeContent === "serie" ? "active" : null,
            ])}
            onClick={() => setActiveContent("serie")}
          >
            <Translate tKey={`Series`} />
          </Button>
          <Button
            variant={`outline-dark`}
            size="sm"
            className={composeClassNames([
              sButton,
              `switch-button`,
              activeContent === "festival" ? "active" : null,
            ])}
            onClick={() => setActiveContent("festival")}
          >
            <Translate tKey={`Festivals`} />
          </Button>
          <Button
            variant={`outline-dark`}
            size="sm"
            className={composeClassNames([
              sButton,
              `switch-button`,
              activeContent === "class" ? "active" : null,
            ])}
            onClick={() => setActiveContent("class")}
          >
            <Translate tKey={`Classes`} />
          </Button>
          {constants.Show.homepageContentStream && (
            <Button
              variant={`outline-dark`}
              size="sm"
              className={composeClassNames([
                sButton,
                `switch-button`,
                activeContent === "stream" ? "active" : null,
              ])}
              onClick={() => setActiveContent("stream")}
            >
              <Translate tKey={`Streams`} />
            </Button>
          )}
          {constants.Show.homepageContentAudio && (
            <Button
              variant={`outline-dark`}
              size="sm"
              className={composeClassNames([
                sButton,
                `switch-button`,
                activeContent === "audio" ? "active" : null,
              ])}
              onClick={() => setActiveContent("audio")}
            >
              <Translate tKey={`Audio`} />
            </Button>
          )}
        </div>
      </div>
      {activeContent === "serie" && series && (
        <VideoListSlider
          data={mapContentToVideoCards(series)}
          contentType={`serie`}
          type={`preview`}
          size="lg"
        />
      )}
      {activeContent === "festival" && series && (
        <VideoListSlider
          data={mapContentToVideoCards(series)}
          contentType={`festival`}
          type={`preview`}
          size="lg"
        />
      )}
      {activeContent === "class" && classes && (
        <VideoListSlider
          data={mapContentToVideoCards(classes)}
          contentType={`class`}
          type={`preview`}
          size="lg"
        />
      )}
      {constants.Show.homepageContentStream &&
        activeContent === "stream" &&
        classes && (
          <VideoListSlider
            data={mapContentToVideoCards(classes)}
            contentType={`stream`}
            type={`preview`}
            size="lg"
          />
        )}
      {constants.Show.homepageContentAudio &&
        activeContent === "audio" &&
        classes && (
          <VideoListSlider
            data={mapContentToVideoCards(classes)}
            contentType={`audio`}
            type={`preview`}
            size="lg"
          />
        )}
      <div className={"show-all"}>
        <div className={composeClassNames([sButtons])}>
          <Button
            onClick={primaryButton?.onClick}
            className={composeClassNames([sButton])}
          >
            <Translate tKey={primaryButton?.title} />
          </Button>
          <Button
            onClick={() =>
              activeSubscription
                ? push(`${constants.Paths.pageClasses}?subscription=true`)
                : push(`${constants.Paths.pageClasses}?paid=true`)
            }
            className={composeClassNames([sButton])}
            variant={"outline-primary"}
          >
            <Translate tKey={`Explore`} /> {numberOfLectures + numberOfPrograms}{" "}
            <Translate tKey={`classes and series`} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ContentOverview;
